import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, money } from "@/lib/api";
import { formatLabel, newLesson, apiError } from "@/lib/campus";
import { LessonEditor } from "@/components/campus/LessonEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = {
  published: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/10 text-red-300 border-red-500/30",
};

const EpisodeDialog = ({ course, onClose, onAdded }) => {
  const [lesson, setLesson] = useState(newLesson(""));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/courses/${course.course_id}/episodes`, { lesson });
      toast.success("Episode added — students see it now");
      onAdded();
    } catch (err) {
      toast.error(apiError(err, "Couldn't add episode"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0E14] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="episode-dialog">
        <DialogHeader><DialogTitle>New {course.format === "series" ? "episode" : "lesson"} · {course.title}</DialogTitle></DialogHeader>
        <LessonEditor lesson={lesson} index={course.lesson_count} label={course.format === "series" ? "Episode" : "Lesson"} onChange={setLesson} />
        <button data-testid="episode-save" onClick={save} disabled={busy || !lesson.title}
          className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Publish
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default function MyTeaching() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [adding, setAdding] = useState(null);
  const load = () => api.get("/instructor/courses").then(({ data }) => setCourses(data)).catch(() => setCourses([]));
  useEffect(() => { load(); }, []);

  const total = (courses || []).reduce((s, c) => s + c.creator_earnings, 0);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-100">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-10" data-testid="my-teaching">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white tracking-tight">My teaching</h1>
            <p className="text-slate-400 text-sm mt-2">Earnings owed to you (93%): <strong className="text-amber-400" data-testid="teaching-total-earnings">{money(total)}</strong> · paid out manually for now.</p>
          </div>
          <button data-testid="teaching-new" onClick={() => navigate("/teach")} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-5 py-3 rounded-xl">
            <Plus className="h-4 w-4" /> New course
          </button>
        </div>
        {courses?.length === 0 && <div className="text-slate-500 text-center py-20" data-testid="teaching-empty">You haven't published anything yet.</div>}
        <div className="space-y-3">
          {(courses || []).map((c) => (
            <div key={c.course_id} data-testid={`teaching-row-${c.course_id}`} className="rounded-xl bg-[#121721] border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS[c.status] || STATUS.pending}`} data-testid={`teaching-status-${c.course_id}`}>{c.status}</span>
                  <span className="text-[11px] text-slate-500">{formatLabel(c.format)} · {c.lesson_count} {c.format === "series" ? "episodes" : "lessons"}</span>
                </div>
                <Link to={`/campus/${c.course_id}`} className="text-white font-semibold hover:text-amber-300 block truncate mt-1">{c.title}</Link>
                {c.status === "rejected" && c.review?.reason && <p className="text-xs text-red-300/80 mt-1">{c.review.reason}</p>}
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div><div className="text-slate-500 text-xs">Students</div><div className="font-mono">{c.enrollments}</div></div>
                <div><div className="text-slate-500 text-xs">You earned</div><div className="font-mono text-amber-400">{money(c.creator_earnings)}</div></div>
                {c.status !== "rejected" && (
                  <button data-testid={`teaching-add-${c.course_id}`} onClick={() => setAdding(c)}
                    className="text-xs border border-white/15 hover:border-amber-500 px-3 py-2 rounded-lg whitespace-nowrap">
                    + {c.format === "series" ? "Episode" : "Lesson"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {adding && <EpisodeDialog course={adding} onClose={() => setAdding(null)} onAdded={() => { setAdding(null); load(); }} />}
    </div>
  );
}
